// hooks/useDraftPersist.js
import { useCallback, useRef } from "react"

const STORAGE_KEY = "chat_drafts"
const MAX_SLOTS = 3

const readStore = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
  } catch {
    return []
  }
}

const writeStore = (drafts) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts))
  } catch {}
}

// Формат: [{ chatId, message, department, messageTheme, savedAt }]

export const useDraftPersist = () => {
  const debounceRef = useRef(null)

  const saveDraft = useCallback((chatId, { message, department, messageTheme }) => {
    if (!chatId) return

    // Ничего не сохраняем, если черновик пустой — удаляем слот
    const isEmpty = !message?.trim() && !department && !messageTheme
    if (isEmpty) {
      clearDraft(chatId)
      return
    }

    const drafts = readStore()
    const idx = drafts.findIndex((d) => d.chatId === String(chatId))

    const entry = {
      chatId: String(chatId),
      message: message || "",
      department: department ?? null,
      messageTheme: messageTheme ?? null,
      savedAt: Date.now(),
    }

    if (idx !== -1) {
      // обновляем существующий
      drafts[idx] = entry
    } else {
      // добавляем новый; если слотов > MAX_SLOTS — убираем самый старый (LRU по savedAt)
      drafts.push(entry)
      if (drafts.length > MAX_SLOTS) {
        const oldestIdx = drafts.reduce(
          (min, d, i) => (d.savedAt < drafts[min].savedAt ? i : min),
          0
        )
        drafts.splice(oldestIdx, 1)
      }
    }

    writeStore(drafts)
  }, [])

  // debounced-версия для onChange полей
  const saveDraftDebounced = useCallback(
    (chatId, data, delay = 500) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => saveDraft(chatId, data), delay)
    },
    [saveDraft]
  )

  const loadDraft = useCallback((chatId) => {
    if (!chatId) return null
    const drafts = readStore()
    return drafts.find((d) => d.chatId === String(chatId)) ?? null
  }, [])

  const clearDraft = useCallback((chatId) => {
    if (!chatId) return
    const drafts = readStore().filter((d) => d.chatId !== String(chatId))
    writeStore(drafts)
  }, [])

  return { saveDraft, saveDraftDebounced, loadDraft, clearDraft }
}
